'use client';

import type { ReactNode } from 'react';

import { Stack, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import type { MarketSignal } from '../utils/market-rankings-signals';


/** 요약 지표 한 칸 — 라벨 · 큰 값 · 부연 */
export function MetricTile({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  color?: string;
}) {
  return (
    <Stack
      spacing={0.25}
      sx={{ p: 1.5, borderRadius: RADIUS.md, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ typography: 'uiTitle', lineHeight: 1.2, color: color || 'text.primary' }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>{sub}</Typography>
      ) : null}
    </Stack>
  );
}

/**
 * 변동 신호 배지 줄.
 *
 * ⚠️ 신호가 없으면 **빈 자리로 두지 않고 그렇게 말한다.** 블록이 통째로 사라지면
 * 켜 둔 항목이 왜 안 보이는지 알 수 없다.
 */
export function SignalBadges({ signals }: { signals: MarketSignal[] }) {
  if (!signals.length) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        직전보다 뚜렷한 변동이 없습니다.
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
      {signals.map((signal) => (
        <Stack
          key={signal.label}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ px: 1, py: 0.4, borderRadius: RADIUS.md, bgcolor: signal.bg, border: '1px solid', borderColor: 'divider' }}
        >
          <Iconify icon={signal.icon} width={15} sx={{ color: signal.color }} />
          <Typography sx={{ fontWeight: 600, typography: 'uiMeta' }}>{signal.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}
